/**
 * Grading Queue — Figma frame 174:1556 (split view) + 175:874 (empty state).
 *
 *   • Masthead   : "N submissions waiting" + sort pill
 *   • Left rail  : queue list with violet-stripe selection cue
 *   • Right pane : exact submitted source, authoritative rubric,
 *                  grade slider (0–100), feedback textarea, and Submit CTA
 *   • Empty state: typographic "0" hero (frame 175:874) when the queue is empty
 *
 * Backend integration:
 *   - usePendingSubmissions()   → queue list
 *   - useGradeSubmission()      → POST /grading/submissions/:id/grade
 *
 * Rubric criterion IDs and maxima come from the selected lab's stored rubric.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  usePendingSubmissions,
  useGradeSubmission,
  useGradingRubric,
} from '../hooks';
import { useToast } from '../components/Toast';
import type { FileItem } from '../types';
import { selectSubmittedFile } from './submittedFiles';
import styles from './Grading.module.css';

function daysAgo(iso: string): number {
  const then = new Date(iso).getTime();
  return Math.max(0, Math.floor((Date.now() - then) / 86_400_000));
}

function waitLabel(iso: string): { text: string; urgent: boolean } {
  const days = daysAgo(iso);
  if (days <= 0) {
    const hrs = Math.floor((Date.now() - new Date(iso).getTime()) / 3_600_000);
    return { text: `Waiting ${Math.max(hrs, 1)}h`, urgent: false };
  }
  return {
    text: `Waiting ${days} ${days === 1 ? 'day' : 'days'}`,
    urgent: days >= 4,
  };
}

export const SubmittedFilesViewer: React.FC<{ files: FileItem[] }> = ({ files }) => {
  const [activePath, setActivePath] = useState(files[0]?.path ?? '');
  const activeFile = selectSubmittedFile(files, activePath);

  if (!activeFile) {
    return (
      <div className={styles.viewerEmpty}>
        No submitted source files are available for this submission.
      </div>
    );
  }

  return (
    <>
      <div className={styles.sourceHeader}>
        <div>
          <span className={styles.sourceEyebrow}>Submitted source</span>
          <span className={styles.sourceNote}>Exact learner submission · no generated diff</span>
        </div>
        <span className={styles.fileCount}>{files.length} file{files.length === 1 ? '' : 's'}</span>
      </div>
      <nav className={styles.fileTabs} aria-label="Submitted files">
        {files.map((file) => (
          <button
            type="button"
            key={file.path}
            className={`${styles.fileTabButton} ${
              file.path === activeFile.path ? styles.fileTabButtonActive : ''
            }`}
            aria-pressed={file.path === activeFile.path}
            onClick={() => setActivePath(file.path)}
          >
            {file.path}
          </button>
        ))}
      </nav>
      <pre
        className={styles.sourceCode}
        aria-label={`Exact submitted source for ${activeFile.path}`}
      >
        <code>{activeFile.content}</code>
      </pre>
    </>
  );
}

const Grading: React.FC = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [feedback, setFeedback] = useState('');

  const { data: pendingResp, isLoading, refetch } = usePendingSubmissions({ limit: 50 });
  const gradeSubmission = useGradeSubmission();
  const toast = useToast();

  const queue = pendingResp?.data ?? [];

  // Auto-select the first item so the viewer is never empty on load.
  useEffect(() => {
    if (!selectedId && queue.length > 0) {
      setSelectedId(queue[0].id);
    }
  }, [queue, selectedId]);

  const selected = useMemo(
    () => queue.find((q) => q.id === selectedId) ?? null,
    [queue, selectedId],
  );
  const {
    data: rubricResponse,
    isLoading: isRubricLoading,
    isError: rubricFailed,
  } = useGradingRubric(selected?.labId);
  const rubric = rubricResponse?.rubric ?? [];

  // Whenever the selection changes, reset the rubric scores. If the backend
  // returned an autoScore we bias the default toward it so instructors start
  // near the machine's estimate rather than at zero.
  useEffect(() => {
    if (!selected || rubric.length === 0) {
      setScores({});
      return;
    }
    const bias = selected.autoScore ?? 0;
    setScores(
      Object.fromEntries(
        rubric.map((criterion) => [
          criterion.id,
          Math.min(
            criterion.maxScore,
            Math.round((bias * criterion.maxScore) / 100),
          ),
        ]),
      ),
    );
    setFeedback('');
  }, [selected, rubricResponse]); // eslint-disable-line react-hooks/exhaustive-deps

  const total = useMemo(
    () => Object.values(scores).reduce((sum, s) => sum + (s || 0), 0),
    [scores],
  );

  const handleGrade = async () => {
    if (!selected || rubric.length === 0) return;
    try {
      await gradeSubmission.mutateAsync({
        submissionId: selected.id,
        data: {
          rubricScores: Object.entries(scores).map(([criteriaId, score]) => ({
            criteriaId,
            score,
          })),
          totalGrade: total,
          feedback,
        },
      });
      toast.showSuccess(`Graded ${selected.studentName}. Nice.`);
      setSelectedId(null);
      refetch();
    } catch {
      toast.showError('Couldn\'t submit that grade — please try again.');
    }
  };

  const handleSkip = () => {
    if (!selected) return;
    const idx = queue.findIndex((q) => q.id === selected.id);
    const next = queue[idx + 1] ?? queue[0];
    setSelectedId(next?.id ?? null);
  };

  // ---------- Empty state ----------
  if (!isLoading && queue.length === 0) {
    return (
      <div className={styles.emptyPage}>
        <div className={styles.emptyMasthead}>
          <span className={styles.eyebrow}>Grading queue</span>
          <h1 className={styles.title}>Nothing waiting</h1>
        </div>
        <div className={styles.emptyCard}>
          <h2 className={styles.emptyZero} aria-hidden="true">0</h2>
          <div className={styles.emptyAccent} aria-hidden="true" />
          <p className={styles.emptyHead}>All caught up.</p>
          <p className={styles.emptySub}>Nothing waiting for review.</p>
          <Link to="/grading?status=graded" className={styles.emptyLink}>
            View graded submissions →
          </Link>
        </div>
        <p className={styles.emptyFooter}>
          Next review window opens when a student submits — you&apos;ll be notified.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* --------------- Masthead --------------- */}
      <header className={styles.masthead}>
        <div className={styles.mastheadCopy}>
          <span className={styles.eyebrow}>Grading queue</span>
          <h1 className={styles.title}>
            {isLoading
              ? 'Loading…'
              : `${queue.length} submission${queue.length === 1 ? '' : 's'} waiting`}
          </h1>
        </div>
        <div className={styles.sortRow}>
          <span className={styles.sortLabel}>Sort:</span>
          <button type="button" className={styles.sortPill}>
            Oldest waiting
          </button>
        </div>
      </header>

      {/* --------------- Split --------------- */}
      <div className={styles.split}>
        {/* Queue */}
        <aside className={styles.queueCard} aria-label="Queue">
          <header className={styles.queueHead}>
            <h2 className={styles.queueTitle}>Queue</h2>
            <span className={styles.queueCount}>{queue.length}</span>
          </header>
          <ul className={styles.queueList}>
            {queue.map((sub) => {
              const wait = waitLabel(sub.submittedAt);
              const active = sub.id === selectedId;
              return (
                <li
                  key={sub.id}
                  className={`${styles.queueItem} ${active ? styles.queueItemActive : ''}`}
                  onClick={() => setSelectedId(sub.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedId(sub.id);
                    }
                  }}
                  aria-current={active ? 'true' : undefined}
                >
                  <span className={styles.queueName}>{sub.studentName}</span>
                  <span className={styles.queueLab}>{sub.labTitle}</span>
                  <span
                    className={`${styles.queueWait} ${wait.urgent ? styles.queueWaitUrgent : ''}`}
                  >
                    {wait.text}
                  </span>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Viewer */}
        <section className={styles.viewerCard} aria-label="Submission viewer">
          {!selected ? (
            <div className={styles.viewerEmpty}>
              <p>Pick a submission from the queue to start reviewing.</p>
            </div>
          ) : (
            <>
              <header className={styles.viewerHead}>
                <div className={styles.viewerCopy}>
                  <h2 className={styles.viewerTitle}>{selected.labTitle}</h2>
                  <span className={styles.viewerSubtitle}>
                    {selected.studentName} · Submitted{' '}
                    {formatDistanceToNowStrict(new Date(selected.submittedAt), { addSuffix: true })}
                  </span>
                </div>
                <div className={styles.viewerActions}>
                  <button type="button" className={styles.btnGhost} onClick={handleSkip}>
                    Skip
                  </button>
                  <button
                    type="button"
                    className={styles.btnPrimary}
                    disabled={gradeSubmission.isPending || isRubricLoading || rubric.length === 0}
                    onClick={handleGrade}
                  >
                    {gradeSubmission.isPending ? 'Saving…' : 'Submit grade'}
                  </button>
                </div>
              </header>

              <div className={styles.viewerBody}>
                {/* Exact submitted source */}
                <div className={styles.sourcePanel}>
                  <SubmittedFilesViewer key={selected.id} files={selected.files ?? []} />
                </div>

                {/* Rubric + grade */}
                <div className={styles.rubricPanel}>
                  <div className={styles.panelSection}>
                    <h3>Rubric</h3>
                    {isRubricLoading ? (
                      <p className={styles.rubricState}>Loading the stored lab rubric…</p>
                    ) : rubricFailed ? (
                      <p className={styles.rubricState}>The stored rubric could not be loaded. Grading is disabled.</p>
                    ) : rubric.length === 0 ? (
                      <p className={styles.rubricState}>No authoritative rubric is configured for this lab. Add one before grading.</p>
                    ) : (
                      <ul className={styles.rubricList}>
                        {rubric.map((criterion) => (
                          <li key={criterion.id} className={styles.rubricScoreRow}>
                            <label htmlFor={`rubric-${criterion.id}`}>
                              <span className={styles.rubricCriterion}>{criterion.category}</span>
                              {criterion.description && (
                                <span className={styles.rubricDescription}>{criterion.description}</span>
                              )}
                            </label>
                            <div className={styles.rubricInputWrap}>
                              <input
                                id={`rubric-${criterion.id}`}
                                type="number"
                                min={0}
                                max={criterion.maxScore}
                                step={1}
                                value={scores[criterion.id] ?? 0}
                                onChange={(event) => {
                                  const value = Number(event.target.value);
                                  setScores((current) => ({
                                    ...current,
                                    [criterion.id]: Math.min(
                                      criterion.maxScore,
                                      Math.max(0, Number.isFinite(value) ? value : 0),
                                    ),
                                  }));
                                }}
                                aria-label={`${criterion.category} score`}
                              />
                              <span>/ {criterion.maxScore}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className={styles.panelSection}>
                    <h3>Grade</h3>
                    <div className={styles.gradeValue}>{total} / 100</div>
                  </div>

                  <div className={styles.panelSection}>
                    <h3>Feedback</h3>
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      placeholder="Anchor the feedback in specific lines — what worked, what to change, and why."
                      className={styles.textarea}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default Grading;
